import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ConfigCacheService } from './config-cache.service';
import { SortEvent } from 'primeng/api';
import { Crypto } from '../helpers/encryption-utils';
import { ProjectAccessVisibility } from '@models/project-access-visibility.model';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  rosterTab = false;
  isEditUnit = false;
  isNewUnit = false;
  isNewRoster = false;
  isEditRoster = false;
  leadsFullPage = false;
  currentProjectName = '';

  constructor(
    private api: ApiService,
    private configCache: ConfigCacheService
  ) {}

  /** Project created, renamed, deleted or duplicated — the office's projects-name-list is stale. */
  private invalidateProjectListCache(): void {
    this.configCache.evict('/projects-name-list');
  }

  saveProject = (payload: any) => {
    return this.api.post('/byd-project', payload).pipe(tap(() => this.invalidateProjectListCache()));
  };
  getProjectList = () => {
    return this.api.get(`/projects-name-list?office_id=${this.api.getOfficeId()}`);
  };
  deleteProject = (id: string) => {
    return this.api.delete('/byd-project/' + id).pipe(tap(() => this.invalidateProjectListCache()));
  };
  EoiShortList = (payload: any) => {
    let office_id = this.api.getOfficeId();
    return this.api.post(`/eoi-short-detail?office_id=${office_id}`, payload);
  };

  updateProject = (id: string, payload: any) => {
    return this.api.put('/byd-project/' + id, payload).pipe(tap(() => this.invalidateProjectListCache()));
  };

  syncDynamicProject = (payload: any) => {
    return this.api.post('/sync-dynamic-project-to-remmi', payload);
  };

  dynamicSyncStatus = (projectId: string) => {
    return this.api.get('/dynamic-sync-status/' + projectId);
  };

  updateCommission = (id: string, payload: any) => {
    return this.api.put('/project-commissions/' + id, payload);
  };

  getCommission = (id: string) => {
    return this.api.get('/get-project-commissions/' + id);
  };

  deleteLot = (payload: any) => {
    return this.api.delete('/project-lot-bulk-delete', payload);
  };

  getListViewAllProject = (payload: any) => {
    return this.api.get('/byd-project-listview', payload);
  };

  getProjectById = (id: any) => {
    return this.api.get('/project/' + id);
  };

  projectById = (id: any) => {
    return this.api.get('/byd-project-by-id/' + id);
  };
  projectFloorPlan = (id: any) => {
    return this.api.get(`/byd-project-by-id/${id}?floorplantotals=1`);
  };
  getGridViewAllProject = (payload: any) => {
    return this.api.get('/byd-project-gridview', payload);
  };

  // Sales/revenue statistics for the project grid cards. Cost is flat in the number of
  // projects, so fetch the whole grid in one call and expand cards client-side rather
  // than calling this per card.
  //
  // Takes no userid — the user is derived from the JWT, and stats are role-scoped from
  // it (admins see the whole project, agents see only their own sales). It also does not
  // accept sub_office, and omits projectassociation_settings, which the grid's access
  // control still needs — hence the callers pair it with getGridViewAllProject rather
  // than replacing that call. Drop the pairing once the backend adds both.
  getProjectSummaryCards = (payload: any) => {
    return this.api.get('/byd-project-summary-cards', payload);
  };

  // NOTE: backend route /byd-project-order is not built yet. Save will 404 until the
  // backend persists { office_id, order: [{ project_id, order }] } and returns projects
  // in that order from getGridViewAllProject (or exposes a display_order to sort by).
  saveProjectOrder = (payload: any) => {
    return this.api.post('/byd-project-order', payload);
  };
  getProjectOrder = () => {
    return this.api.get(`/byd-project-order?office_id=${this.api.getOfficeId()}`);
  };

  // Per-user, per-office hide/show for a project. Upserts, so calling it repeatedly
  // is safe. payload: { user_id, office_id, is_hidden }. Default (no row) = shown, so
  // the caller only hits this when the user actually flips the toggle.
  setProjectVisibility = (projectId: string, payload: any) => {
    return this.api.post(`/project-visibility/${projectId}`, payload);
  };

  // Admin-controlled "who may see this project" (All Users, or any mix of Selected Roles / Users / Teams).
  // Not the same thing as setProjectVisibility above, which is one user's own hide/show.
  // Backend contract: docs/backend/projects/project-access-visibility.md
  getProjectAccessVisibility = (projectId: string) => {
    return this.api.get(`/project-access-visibility/${projectId}`);
  };

  saveProjectAccessVisibility = (projectId: string, visibility: ProjectAccessVisibility) => {
    return this.api.put(`/project-access-visibility/${projectId}`, {
      ...visibility,
      office_id: this.api.getOfficeId(),
      updated_by: this.api.getUserId(),
    });
  };

  /** Mirrors the backend's check on the PUT above — everyone else sees the setting read-only. */
  canManageAccessVisibility = (): boolean => {
    const role = (this.api.getRole() ?? '').toLowerCase();
    const permissions = Crypto.decrypt(localStorage.getItem('_per')) || '';
    return role === 'master admin' || role === 'operations manager' || permissions.includes('allprojects.manage');
  };

  canViewProject = (projectId: string) => {
    return this.api.get(`/project-access-visibility/${projectId}/can-view`);
  };

  // Per-user, per-office hide/show for a precinct. Mirrors setProjectVisibility exactly —
  // upserts, default (no row) = shown, so only called when the user flips the toggle.
  // payload: { user_id, office_id, is_hidden }.
  setPrecinctVisibility = (precinctId: string, payload: any) => {
    return this.api.post(`/precinct-visibility/${precinctId}`, payload);
  };

  // The hidden set up front (e.g. a "Manage hidden precincts" screen). Usually
  // unnecessary — is_hidden on the my-precint list already carries per-precinct state.
  getPrecinctVisibility = () => {
    return this.api.get(`/precinct-visibility?user_id=${this.api.getUserId()}&office_id=${this.api.getOfficeId()}`);
  };

  getProjectSummaryById = (projectId: string = '', agentId: string = '') => {
    return this.api.get(`/projects-dashboard-summary?projectid=${projectId}&officeid=${this.api.getOfficeId()}&agent_responsible=${agentId}`);
  };

  getProjectBranding = (id: string) => {
    return this.api.get('/project-branding/' + id);
  };
  getUnitByLevel = (project_id: string = '', level: string = '') => {
    return this.api.get(`/get-filter-lots?office_id=${this.api.getOfficeId()}&project_id=${project_id}&level=${level}`);
  };
  getUnitIncludeFloorplan = (id: string) => {
    return this.api.get(`/project-lot/${id}?include=floorplan`);
  };
  addProjectBranding = (payload: any) => {
    return this.api.post('/project-branding', payload);
  };
  // Precinct twins of the two above — same payload and response shape, keyed on `precinct_id`.
  // Used by the precinct create/edit dialog (My Precincts) to give a precinct its own logo and
  // colours, which Precinct Insight reports then pick up by default.
  getPrecinctBranding = (precinctId: string) => {
    return this.api.get('/precinct-branding/' + precinctId);
  };
  addPrecinctBranding = (payload: any) => {
    return this.api.post('/precinct-branding', payload);
  };
  duplicateProject = (payload: any) => {
    return this.api.post(`/byd-projects-duplicates`, payload).pipe(tap(() => this.invalidateProjectListCache()));
  };
  // `officeId` overrides the caller's active office. Reports need that: a Precinct Insight report
  // carries its own `office_id` and a multi-office user's active office can differ from it, so the
  // report's precinct picker passes ReReportService.officeId here. Omitted everywhere else, which
  // keeps the original active-office behaviour.
  getPrecinct = (officeId: string | null = null) => {
    return this.api.get(`/my-precint?office_id=` + (officeId || this.api.getOfficeId()));
  };
  deletePrecinct = (id: string) => {
    return this.api.delete(`/my-precint/${id}`);
  };
  createPrecinct = (payload: any) => {
    return this.api.post(`/my-precint`, payload);
  };
  updatePrecinct = (id: string, payload: any) => {
    return this.api.put(`/my-precint/${id}`, payload);
  };
  updatePrecinctAllocateStatus = (id: string, payload: any) => {
    return this.api.put(`/my-precint-projects/${id}`, payload);
  };
  getProjectShortDetails = (id: string) => {
    return this.api.get(`/project-short-detail-new/${id}`);
  };

  lockProject = (payload: any) => {
    return this.api.post(`/project-lock/`, payload);
  };

  getLockStatus = (id: string) => {
    return this.api.get(`/project-unlock-status/${id}`);
  };

  unlockProject = (pid: string, uid: string) => {
    return this.api.get(`/project-unlock/${pid}/${uid}`);
  };

  getLotPreview = (project_id: string) => {
    return this.api.get(`/lot-preview/${project_id}?user_id=${this.api.getUserId()}`);
  };

  getEoiList = (id: string) => {
    return this.api.get(`/lot-expression-of-interest/${id}`);
  };

  sendFloorPlanEmail = (payload: any) => {
    return this.api.post(`/floorplan-email-send`, payload);
  };

  addProjectFloorPlan = (project_id: string, payload: any) => {
    return this.api.post(`/project-floorplan-add/${project_id}?office_id=${this.api.getOfficeId()}&user_id=${this.api.getUserId()}`, payload);
  };

  getProjectLevelList = (id: string) => {
    return this.api.get('/project-lot-levels/' + id);
  };
  getProjectLevel = (id: string) => {
    return this.api.get('/projects-level-order/' + id);
  };

  allocateProjectLevelWithFloorPlates = (id: string, payload: any) => {
    return this.api.post('/projects-level-order/' + id, payload);
  };

  getProjectLotById = (id: string) => {
    return this.api.get('/project-lot/' + id);
  };
  getProjectLotList = (payload: any) => {
    return this.api.post(`/get-all-units`, payload);
  };
  updateProjectLotById = (id: string, payload: any) => {
    return this.api.put('/project-lot/' + id, payload);
  };

  saveProjectLot = (payload: any) => {
    return this.api.post('/project-lot', payload);
  };

  getUnitFloorPlan = (_id: string, project_id: string) => {
    return this.api.get(`/unit-floorplans/${_id}?office_id=${this.api.getOfficeId()}&project_id=${project_id}`);
  };

  getUnitDetails = (_id: string) => {
    return this.api.get('/get-unit-detail/' + _id);
  };

  getWeeklyProjectSale = (userId: any) => {
    return this.api.get(
      `/project-weekly-sale-activity/` + Crypto.decrypt(localStorage.getItem('pid')) + `${userId?.length > 0 ? `?agent_id=${userId}` : ''}`
    );
  };

  getTopSellingAgencyStats = () => {
    return this.api.get('/top-selling-agency-stats/' + Crypto.decrypt(localStorage.getItem('pid')));
  };

  getProjectSummary = (userId: any) => {
    return this.api.get(`/project-summary/${Crypto.decrypt(localStorage.getItem('pid'))}` + `${userId?.length > 0 ? `?agent_id=${userId}` : ''}`);
  };

  /**
   * Project → Reporting → Summary. One call serves the whole tab (key metrics, the three
   * donuts, the weekly trend and the pipeline table).
   *
   * NOTE: this is not the same source as `getProjectSummary` above. Where the two overlap
   * they are meant to agree, except FIRB — `/project-summary/:id` matches the stored value
   * case-sensitively and therefore reports 0, while this endpoint reports the real figure.
   * If that older endpoint is ever fixed, its FIRB number will jump from 0 to a real value.
   *
   * `params`: project_id and officeid are required; scope (all_sales | net_sales |
   * back_to_market), date_from/date_to and status_reason are optional — the dates bound the
   * trend only, and status_reason narrows the whole payload to one pipeline status (empty
   * means every status).
   */
  getProjectReportingSummary = (params: any) => {
    return this.api.get('/project-reporting-summary', params);
  };

  /**
   * Project → Reporting → Sales by Channel. One call serves the whole tab.
   *
   * Same scoping and the same channel figures as `getProjectReportingSummary` above — the
   * counts here are meant to equal that tab's channel donut, so the two must move together.
   *
   * `params`: project_id and officeid are required; scope (all_sales | net_sales |
   * back_to_market) is optional.
   */
  getProjectReportingChannel = (params: any) => {
    return this.api.get('/project-reporting-channel', params);
  };

  /**
   * Project → Reporting → Back-to-Market. One call serves the whole tab.
   *
   * The back-to-market rule is the same helper the Summary tab uses, so `meta.btm_events`
   * must equal that tab's back_to_market tile.
   *
   * `params`: project_id and officeid are required; date_from/date_to are optional and bound
   * the trend only. `scope` is deliberately not sent — every section on this tab is about
   * back-to-market events by definition, so the endpoint treats it as a no-op.
   */
  getProjectReportingBtm = (params: any) => {
    return this.api.get('/project-reporting-btm', params);
  };

  /**
   * Project → Reporting → Sales by Product Type. One call serves both rings.
   *
   * `params`: project_id and officeid are required; scope (all_sales | net_sales |
   * back_to_market) is optional and moves the SOLD ring only — availability is always
   * "available right now", as inventory is on every other tab.
   */
  getProjectReportingProductType = (params: any) => {
    return this.api.get('/project-reporting-product-type', params);
  };

  /**
   * Project → Reporting → FIRB vs Local. One call serves the whole tab.
   *
   * `firb` is a Yes/No recorded on the EOI — not estimated, not inferred. Rows holding
   * something unreadable are surfaced as an explicit Unknown slice rather than dropped, so
   * the parts always reconcile to the total.
   *
   * `params`: project_id and officeid are required; scope (all_sales | net_sales |
   * back_to_market) is optional.
   */
  getProjectReportingFirb = (params: any) => {
    return this.api.get('/project-reporting-firb', params);
  };

  getProjectMonthlySale = (params: any) => {
    return this.api.get('/project-monthly-sale-activity/' + Crypto.decrypt(localStorage.getItem('pid')), params);
  };

  getProjectAssociation = (id: any) => {
    return this.api.get(`/association/getbyproject/${id}`);
  };

  checkUserProjectActivity = (id: any) => {
    return this.api.get(`/project-activity-user?project_id=${id}&office_id=${this.api.getOfficeId()}`);
  };

  deleteEOI = (payload: any) => {
    return this.api.delete('/expression-of-interest-bulk-delete', payload);
  };

  getExpressionOfInterestById = (id: any, includeLookups: boolean = false) => {
    return this.api.get(`/expression-of-interest/${id}?include_lookups=${includeLookups}`);
  };

  // Deactivated EOIs (records deleted in Dynamics, moved to archive collection). Read-only.
  getDeactivatedEoiList = (params: any) => {
    return this.api.getCancel('/expression-of-interest-deactivated-getall', params);
  };

  deleteBulkEoi = (id: any) => {
    return this.api.post(`/eng/misc/entity/bulkdelete/${'expression_of_interest'}?office_id=${this.api.getOfficeId()}`, { selected: id });
  };

  getExpressOfInterestList = (payload: any) => {
    return this.api.post('/get-all-eois', payload);
  };
  saveExpressionOfInterest = (id: any) => {
    return this.api.post('/expression-of-interest', id);
  };

  updateExpressionOfInterest = (id: any, payload: any) => {
    if (!id) {
      return null;
    }
    return this.api.put('/expression-of-interest/' + id, payload);
  };

  bulkEditEoi = (payload: any) => {
    return this.api.put('/eng/misc/entity/bulkedit', payload);
  };

  getCarParkStorage = (id: any) => {
    return this.api.get(`/get-carpark-storage/${id}`);
  };

  getProjectType = (id: any) => {
    return this.api.get(`/project-unit-types/${id}`);
  };

  lockAllocationForcedLock = (payload: any) => {
    return this.api.post('/lock-allocation-forcedlock', payload);
  };

  lockAllocationStats = (payload: any) => {
    return this.api.post('/lock-allocation-status', payload);
  };

  lockAllocation = (payload: any) => {
    return this.api.post('/lock-allocation', payload);
  };

  assignAllocation = (payload: any) => {
    return this.api.post('/unit-allocation', payload);
  };

  getAllocation = (project_id: any) => {
    return this.api.get(`/get-allocations?office_id=${this.api.getOfficeId()}&project_id=${project_id}`);
  };

  lockCarPark = (payload: any) => {
    return this.api.post('/lock-carpark', payload);
  };

  lockCarParkStatus = (payload: any) => {
    return this.api.post('/lock-carpark-status', payload);
  };

  lockCarParkStatusForcedLock = (payload: any) => {
    return this.api.post('/lock-carpark-forcedlock', payload);
  };

  storeCarParks = (payload: any) => {
    return this.api.post('/store-carpark-storage', payload);
  };

  deleteAllocations = (payload: any) => {
    return this.api.post('/unit-deallocation', payload);
  };

  getProjectAgreement = (project_id: any) => {
    return this.api.get('/project-agreement/' + project_id);
  };

  getProjectAgreementById = (id: any) => {
    return this.api.get('/project-agreement-detail/' + id);
  };

  updateProjectAgreement = (id: any, payload: any) => {
    return this.api.put('/project-agreement/' + id, payload);
  };

  saveProjectAgreement = (payload: any) => {
    return this.api.post('/project-agreement', payload);
  };

  deleteProjectAgreement = (id: any) => {
    return this.api.delete('/project-agreement/' + id);
  };

  saveProjectTime = (payload: any) => {
    return this.api.post('/project-appt-setting', payload);
  };

  getProjectTime = (project_id: any) => {
    return this.api.get('/project-appt-setting/' + project_id);
  };

  customizeProjectTag = (payload: any) => {
    return this.api.post('/general/tag-customization', payload);
  };

  getProjectEntity = (inc: string = '') => {
    return this.api.get('/eng/getall?office_id=' + this.api.getOfficeId() + inc);
  };

  paseToCsv = (projectId: string, payload: any) => {
    return this.api.post(`/eng/misc/units/parsecsv2/byd_projects/${projectId}`, payload);
  };

  deleteRoster(id: any) {
    return this.api.post('/roster/delete', {
      byd_rosterid: id,
    });
  }

  rosterByID(id: any) {
    return this.api.get('/project/rosters/' + id);
  }

  getLeadAssignments(projectId: any) {
    return this.api.get('/leadassign/getbyproject/' + projectId);
  }

  getAssociation(projectId: any) {
    return this.api.get('/association/getbyproject/' + projectId);
  }

  newRoster(payload) {
    return this.api.post('/roster/create', payload);
  }

  updateRoster(payload: any) {
    return this.api.post('/roster/update', payload);
  }

  postLeadAssignment(payload: any) {
    return this.api.post('/leadassign/create', payload);
  }

  postAssociation(payload: any) {
    return this.api.post('/association/create', payload);
  }

  customSort(event: SortEvent) {
    event.data.sort((data1, data2) => {
      let value1 = data1[event.field];
      let value2 = data2[event.field];
      let result = null;

      if (value1 == null && value2 != null) result = -1;
      else if (value1 != null && value2 == null) result = 1;
      else if (value1 == null && value2 == null) result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string') result = value1.localeCompare(value2);
      else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;

      return event.order * result;
    });
  }

  saveGeneralColumn = (sp: boolean = false, payload: any) => {
    return sp == true
      ? this.api.post('/project-column-visibility/' + payload?.project_id, payload)
      : this.api.post('/general/add-visibility-column', payload);
  };

  getGeneralColumn = (sp: boolean = false, project_id: string = '') => {
    return sp == true
      ? this.api.get(`/project-column-visibility/${project_id}`)
      : this.api.get(`/general/get-visibility-column?office_id=${this.api.getOfficeId()}&type=${'project_units'}`);
  };

  saveProjectNotification = (payload: any) => {
    return this.api.post(`/project-notification-setup`, payload);
  };

  getProjectNotificationById = (projectId: any) => {
    return this.api.get(`/project-notification-setup?project_id=${projectId}`);
  };

  getProjectUserByRole = (project_id: any, role: string) => {
    return this.api.get(`/project-associated-users-by-role/${project_id}?role=${role}`);
  };
  getDeveloper = (project_id: any) => {
    return this.api.get(`/project-agreement-developer-contacts/${project_id}`);
  };
}
